import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import { CheckBox } from '../../atoms/CheckBox';
import { Heading } from '../../atoms/Heading';
import { headingType, size, tags } from '../../utils/enums/enums';
import { CheckboxControlsListProps, ControlsProps } from './CheckboxControlsList.types';
import { StyledCheckboxControlsList } from './CheckboxControlsList.styles';

const renderControl = (
  control: ControlsProps,
  selectedControls: ControlsProps[],
  handleControlToggle: (control: ControlsProps) => void,
  handleKeyDown: (event: React.KeyboardEvent, control: ControlsProps) => void,
  name: string,
  index: number,
  custom_click_track_value: string,
  clickTrackingLoc: string,
  clickTrackingVal: string,
  colLength: number,
  selectedAmenityTooltip: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedAmenityTooltip?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipRefElement?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTooltipContent?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hideTooltip?: any,
  setShowToolTip?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const controlId = `${name.replace(/ /g, '')}-${index}`;
  const isTooltipDataAvailable = control?.tooltipData && control?.tooltipData?.tooltipText;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setToolTip = (event: any, control: ControlsProps) => {
    if (event.key === 'Enter' || event.keyCode === 13 || event.type === 'click') {
      setTooltipContent(control?.tooltipData);
      setShowToolTip?.(true);
      setSelectedAmenityTooltip(control?.code as string);
    }
  };
  const isTouchDevice = () => window.matchMedia('(pointer: coarse)').matches;

  return (
    <div className={`${control?.showTooltip ? 'tooltip-checkbox-item' : 'checkbox-item'} col-${colLength || 6}`}>
      <CheckBox
        key={controlId}
        checkboxId={controlId}
        checkboxName={name}
        checkboxLabel={control?.count ? control?.label + ` (${control?.count})` : control?.label}
        checked={selectedControls.some((item: ControlsProps) => item?.label === control?.label)}
        onChange={() => handleControlToggle(control)}
        onKeyDown={event => handleKeyDown(event, control)}
        setTabIndex={0}
        value={control?.code || control?.label || ''}
        className={`checkbox-controls-list__checkbox pb-2`}
        inputClassName="checkbox-input custom_click_track"
        {...{
          custom_click_track_value: custom_click_track_value
            ? custom_click_track_value
            : clickTrackingLoc &&
              `${clickTrackingLoc} | ${clickTrackingVal} ${control?.label} ${
                selectedControls.some((item: ControlsProps) => item?.label === control?.label)
                  ? 'selection'
                  : 'de-selection'
              } | internal`,
        }}
        disabled={control?.disabled}
        iconClassName={control?.iconClass}
      >
        {control?.showTooltip && (
          <span className="tooltip-information">
            <span
              id={`hotel-directory-tooltip-${controlId}`}
              className="m-link-icon-button-info tooltip-icon"
              onClick={(e): void => {
                e.stopPropagation();
                e.preventDefault();
                setToolTip(e, control);
              }}
              onKeyDown={(e): void => {
                //perform below functionality only on ENTER keypress as we need the TAB keypress to work as it is
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  e.preventDefault();
                  setToolTip(e, control);
                }
              }}
              onMouseEnter={() => {
                setTooltipContent(control?.tooltipData);
                setShowToolTip?.(true);
                setSelectedAmenityTooltip(control?.code as string);
              }}
              onMouseLeave={() => {
                !isTouchDevice() && setShowToolTip?.(false);
                setSelectedAmenityTooltip('');
              }}
              onScroll={() => {
                setShowToolTip?.(false);
                setSelectedAmenityTooltip('');
              }}
              onBlur={() => hideTooltip()}
              ref={selectedAmenityTooltip === control?.code ? tooltipRefElement : null}
              aria-label={control?.label}
              role="tooltip"
              tabIndex={0}
              aria-describedby={control?.tooltipContainerId ?? ''}
            ></span>
          </span>
        )}
      </CheckBox>
    </div>
  );
};
export const CheckboxControlsList: React.FC<CheckboxControlsListProps> = ({
  title,
  subTitle = '',
  eyebrowText = '',
  controls,
  enableShowMoreToggle = false,
  showMoreCount = 0,
  onChange,
  defaultSelected = [],
  name,
  showMoreLabel = 'Show More',
  showLessLabel = 'Show Less',
  showSeparator = false,
  colLength = 6,
  custom_click_track_value = '',
  clickTrackingLoc = '',
  clickTrackingVal = '',
  tooltipRefElement,
  setTooltipContent,
  modalRefElement,
  setShowToolTip,
}) => {
  const [selectedControls, setSelectedControls] = useState<ControlsProps[]>(defaultSelected);
  const [showMore, setShowMore] = useState(true);

  useEffect(() => {
    setSelectedControls(defaultSelected);
  }, [defaultSelected]);

  const handleControlToggle = (control: ControlsProps) => {
    let updatedSelection: ControlsProps[] = [];

    if (selectedControls.some((item: ControlsProps) => item.label === control?.label)) {
      updatedSelection = selectedControls.filter(ctl => ctl.label !== control?.label);
    } else {
      updatedSelection.push(...selectedControls, control);
    }

    setSelectedControls(updatedSelection);
    onChange(updatedSelection);
  };

  const handleKeyDown = (event: React.KeyboardEvent, control: ControlsProps) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleControlToggle(control);
    }
  };

  const [selectedAmenityTooltip, setSelectedAmenityTooltip] = useState<string>('');

  const renderedControls = !enableShowMoreToggle || !showMore ? controls : controls.slice(0, showMoreCount);
  const btnLabel = !showMore ? showLessLabel : showMoreLabel;

  useEffect(() => {
    const modalContent = modalRefElement?.current;
    if (modalContent) {
      modalContent.addEventListener('scroll', hideTooltip);
    }
  }, [modalRefElement]);

  // Hide tooltip when focus moves away from the icon or tooltip
  const hideTooltip = () => {
    setShowToolTip?.(false);
    setSelectedAmenityTooltip('');
  };

  return (
    <StyledCheckboxControlsList
      data-component-name="m-ui-library-CheckboxControlsList"
      data-testid="ui-library-CheckboxControlsList"
      className={`col-12 checkbox-controls-list`}
    >
      <fieldset>
        {title && (
          <div className="my-4 mt-md-5 mb-md-4 mt-lg-5 mb-lg-4 mt-xl-5 mb-xl-4">
            <legend>
              <Heading
                variation={headingType.subtitle}
                titleText={title}
                fontSize={size.large}
                element={tags.h4}
                customClass={'checkbox-controls-list__title'}
              ></Heading>
            </legend>
          </div>
        )}
        {subTitle && (
          <Heading
            variation={headingType.subtitle}
            titleText={subTitle}
            fontSize={size.large}
            element={tags.h5}
            customClass={'pt-0 pb-3'}
          ></Heading>
        )}
        {eyebrowText && <p className="t-label-xs filter-sub-heading mb-0 pb-3 pt-0">{eyebrowText}</p>}
        <div className="row">
          {renderedControls.map((control, index) =>
            renderControl(
              control,
              selectedControls,
              handleControlToggle,
              handleKeyDown,
              name,
              index,
              custom_click_track_value,
              clickTrackingLoc,
              clickTrackingVal,
              colLength,
              selectedAmenityTooltip,
              setSelectedAmenityTooltip,
              tooltipRefElement,
              setTooltipContent,
              hideTooltip,
              setShowToolTip
            )
          )}
        </div>
        {enableShowMoreToggle && controls.length > showMoreCount && (
          <button
            className="show-more-btn t-font-s p-0 d-flex custom_click_track"
            onClick={() => setShowMore(!showMore)}
            custom_click_track_value={
              clickTrackingLoc && `${clickTrackingLoc} | ${showMore ? showLessLabel : showMoreLabel} | internal`
            }
          >
            {btnLabel}{' '}
            <span
              className={clsx('show-more-icon', { 'icon-dropdown-down': showMore, 'icon-dropdown-up': !showMore })}
            ></span>
          </button>
        )}
        {showSeparator && <hr className="filter-separator mt-4 mt-md-5 mt-lg-5 mt-xl-5" />}
      </fieldset>
    </StyledCheckboxControlsList>
  );
};
